import React, { useState, useMemo } from 'react';
import { ArrowRight, Check, Star, Clock, Play, Briefcase, Film, MessageCircle } from 'lucide-react';
import { G2, NavyBg, PAGE_ROOT, OR, OG } from '../../config/styles';

/* غلاف الصفحة بخلفية الأكاديمية الكحلي + الكرات الطايرة */
function Shell({ children }) {
  return (
    <div style={PAGE_ROOT}>
      <NavyBg />
      {children}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   مشغّل فيديو آمن (يخفي مصدر الفيديو قدر الإمكان)
   ════════════════════════════════════════════════════════════ */
function SafeVideo({ url, height = '100%' }) {
  const raw = String(url || '').trim();
  if (!raw) return null;

  const box = { position: 'relative', width: '100%', height, overflow: 'hidden', background: '#000', userSelect: 'none', borderRadius: '14px' };

  const vMatch = raw.match(/vimeo\.com\/(\d+(?:\/[a-f0-9]+)?)/i);
  if (vMatch) {
    const [numId, hash] = vMatch[1].split('/');
    const src = hash
      ? `https://player.vimeo.com/video/${numId}?h=${hash}&color=f97316&byline=0&portrait=0&title=0&dnt=1`
      : `https://player.vimeo.com/video/${numId}?color=f97316&byline=0&portrait=0&title=0&dnt=1`;
    return (
      <div style={box} onContextMenu={e => e.preventDefault()}>
        <iframe src={src} style={{ width: '100%', height: '100%', border: 'none' }} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="video" />
      </div>
    );
  }

  const yMatch = raw.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/i);
  if (yMatch) {
    return (
      <div style={box} onContextMenu={e => e.preventDefault()}>
        <iframe src={`https://www.youtube-nocookie.com/embed/${yMatch[1]}?rel=0&modestbranding=1`} style={{ width: '100%', height: '100%', border: 'none' }} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="video" />
        <div style={{ position: 'absolute', top: 0, right: 0, width: '120px', height: '46px', zIndex: 3 }} />
      </div>
    );
  }

  const dMatch = raw.match(/\/d\/([a-zA-Z0-9_-]+)/);
  const driveId = dMatch ? dMatch[1] : (/^[a-zA-Z0-9_-]{20,}$/.test(raw) ? raw : null);
  if (driveId) {
    return (
      <div style={box} onContextMenu={e => e.preventDefault()}>
        <iframe src={`https://drive.google.com/file/d/${driveId}/preview`} style={{ width: '100%', height: '100%', border: 'none' }} allow="autoplay" allowFullScreen title="video" />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '46px', zIndex: 3, background: 'transparent' }} />
      </div>
    );
  }

  return (
    <div style={box} onContextMenu={e => e.preventDefault()}>
      <iframe src={raw} style={{ width: '100%', height: '100%', border: 'none' }} allowFullScreen title="video" />
    </div>
  );
}

/* صف نجوم */
function Stars({ n = 5, size = 14 }) {
  return (
    <span style={{ display: 'inline-flex', gap: '1px' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={size} fill={i <= n ? '#fbbf24' : 'none'} color={i <= n ? '#fbbf24' : '#52607a'} />
      ))}
    </span>
  );
}

/* ════════════════════════════════════════════════════════════
   صفحة تفاصيل الباقة الواحدة
   ════════════════════════════════════════════════════════════ */
function PackageDetail({ service, reviews, lang, isRTL, t, onBack, onOpenWA, onSubmitReview }) {
  const ar = lang === 'ar';
  const name = ar ? service.name_ar : (service.name_en || service.name_ar);
  const desc = ar ? service.description_ar : (service.description_en || service.description_ar);
  const duration = ar ? service.duration_ar : (service.duration_en || service.duration_ar);
  const features = Array.isArray(service.features) ? service.features : [];
  const myReviews = (reviews || []).filter(r => r.service_id === service.id && r.is_approved !== false)
    .sort((a, b) => (a.order_num || 0) - (b.order_num || 0));

  const [rvName, setRvName]   = useState('');
  const [rvRate, setRvRate]   = useState(5);
  const [rvText, setRvText]   = useState('');
  const [rvBusy, setRvBusy]   = useState(false);
  const [rvDone, setRvDone]   = useState(false);

  const submitReview = async () => {
    if (!rvName.trim()) { alert(ar ? 'اكتب اسمك' : 'Enter your name'); return; }
    if (!onSubmitReview) return;
    setRvBusy(true);
    const res = await onSubmitReview({
      service_id: service.id,
      client_name: rvName.trim(),
      rating: Number(rvRate) || 5,
      comment_ar: rvText.trim() || null,
    });
    setRvBusy(false);
    if (res?.ok) { setRvDone(true); setRvName(''); setRvRate(5); setRvText(''); }
    else alert(ar ? 'حصل خطأ، حاول تاني' : 'Error, try again');
  };

  const fieldStyle = { padding: '11px 14px', border: '1.5px solid rgba(255,255,255,0.18)', borderRadius: '10px', fontSize: '14px', fontFamily: "'Tajawal', sans-serif", outline: 'none', background: 'rgba(255,255,255,0.95)', color: '#1f2937' };

  return (
    <Shell>
      <button onClick={onBack}
        style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: '10px', padding: '8px 16px', color: 'white', cursor: 'pointer', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '18px', fontWeight: 'bold' }}>
        <ArrowRight size={15} style={{ transform: isRTL ? 'none' : 'rotate(180deg)' }} /> {ar ? 'رجوع للباقات' : 'Back to packages'}
      </button>

      <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '22px', overflow: 'hidden', boxShadow: '0 18px 50px rgba(0,0,0,0.34)', animation: 'cardRise .5s ease both' }}>
        {/* الهيدر */}
        <div style={{ background: G2, padding: '34px 28px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-30px', [isRTL ? 'left' : 'right']: '-20px', fontSize: '130px', opacity: 0.14 }}>{service.icon || '💼'}</div>
          {service.is_featured && (
            <div style={{ position: 'absolute', top: '14px', [isRTL ? 'right' : 'left']: '14px', background: '#fbbf24', color: '#78350f', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
              ⭐ {ar ? 'الأكثر طلبًا' : 'Most Popular'}
            </div>
          )}
          <div style={{ fontSize: '64px', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.25))' }}>{service.icon || '💼'}</div>
          <h2 style={{ color: 'white', fontSize: '26px', fontWeight: 'bold', margin: '10px 0 0' }}>{name}</h2>
        </div>

        <div style={{ padding: '26px' }}>
          {/* السعر + المدة */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '22px' }}>
            <div style={{ flex: 1, minWidth: '140px', background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.25)', borderRadius: '14px', padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#fb923c', fontWeight: 'bold', marginBottom: '4px' }}>{ar ? 'السعر يبدأ من' : 'Starts from'}</div>
              <div style={{ fontSize: '30px', fontWeight: 'bold', color: '#fff' }}>${service.price}</div>
            </div>
            {duration && (
              <div style={{ flex: 1, minWidth: '140px', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '14px', padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#34d399', fontWeight: 'bold', marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><Clock size={13} /> {ar ? 'مدة التنفيذ' : 'Duration'}</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff' }}>{duration}</div>
              </div>
            )}
          </div>

          {/* الشرح */}
          {desc && (
            <div style={{ marginBottom: '22px' }}>
              <h3 style={{ fontSize: '17px', fontWeight: 'bold', color: '#fff', marginBottom: '8px' }}>📋 {ar ? 'تفاصيل الباقة' : 'Package details'}</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.9', color: 'rgba(255,255,255,0.74)', whiteSpace: 'pre-wrap', margin: 0 }}>{desc}</p>
            </div>
          )}

          {/* المميزات */}
          {features.length > 0 && (
            <div style={{ marginBottom: '22px' }}>
              <h3 style={{ fontSize: '17px', fontWeight: 'bold', color: '#fff', marginBottom: '12px' }}>✨ {ar ? 'اللي هتحصل عليه' : "What you'll get"}</h3>
              <div style={{ display: 'grid', gap: '8px' }}>
                {features.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '11px 14px' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'linear-gradient(135deg,#10b981,#059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Check size={13} color="white" strokeWidth={3} />
                    </div>
                    <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.85)', fontWeight: '500' }}>{typeof f === 'string' ? f : (f.text || '')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* الفيديو التوضيحي */}
          {service.video_url && (
            <div style={{ marginBottom: '22px' }}>
              <h3 style={{ fontSize: '17px', fontWeight: 'bold', color: '#fff', marginBottom: '6px' }}>🎬 {ar ? 'انت هتستلم نفس ده بالظبط' : "This is exactly what you'll receive"}</h3>
              <div style={{ height: '300px', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                <SafeVideo url={service.video_url} />
              </div>
            </div>
          )}

          {/* آراء العملاء */}
          <div style={{ marginBottom: '22px' }}>
            <h3 style={{ fontSize: '17px', fontWeight: 'bold', color: '#fff', marginBottom: '12px' }}>💬 {ar ? 'آراء العملاء' : 'Client reviews'}</h3>
            {myReviews.length > 0 ? (
              <div style={{ display: 'grid', gap: '12px' }}>
                {myReviews.map(r => (
                  <div key={r.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '14px', padding: '14px 16px', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      {r.avatar_url
                        ? <img src={r.avatar_url} alt={r.client_name} style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover' }} />
                        : <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: OG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '15px' }}>{(r.client_name || '؟').charAt(0)}</div>}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>{r.client_name}</div>
                        <Stars n={r.rating || 5} size={12} />
                      </div>
                    </div>
                    {r.comment_ar && <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', lineHeight: '1.8', margin: 0 }}>{r.comment_ar}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', margin: '0 0 4px' }}>{ar ? 'كن أول من يقيّم هذه الباقة 👇' : 'Be the first to review 👇'}</p>
            )}

            {/* فورم رأي العميل */}
            {onSubmitReview && (
              <div style={{ marginTop: '16px', background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '16px', padding: '18px' }}>
                {rvDone ? (
                  <div style={{ textAlign: 'center', padding: '12px' }}>
                    <div style={{ fontSize: '40px', marginBottom: '8px' }}>✅</div>
                    <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#34d399', margin: '0 0 4px' }}>{ar ? 'شكرًا لرأيك!' : 'Thanks for your review!'}</p>
                    <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', margin: 0 }}>{ar ? 'رأيك تحت المراجعة وهيظهر قريبًا بعد الموافقة' : 'Your review is under review and will appear soon'}</p>
                  </div>
                ) : (
                  <>
                    <h4 style={{ fontSize: '15px', fontWeight: 'bold', color: '#fb923c', margin: '0 0 12px' }}>✍️ {ar ? 'أضف رأيك في الباقة' : 'Add your review'}</h4>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      <input value={rvName} onChange={e => setRvName(e.target.value)} placeholder={ar ? 'اسمك' : 'Your name'}
                        style={{ ...fieldStyle, flex: 1, minWidth: '140px' }} />
                      <select value={rvRate} onChange={e => setRvRate(e.target.value)}
                        style={{ ...fieldStyle, cursor: 'pointer' }}>
                        {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{'⭐'.repeat(n)}</option>)}
                      </select>
                    </div>
                    <textarea value={rvText} onChange={e => setRvText(e.target.value)} placeholder={ar ? 'رأيك في الخدمة (اختياري)' : 'Your feedback (optional)'} rows="3"
                      style={{ ...fieldStyle, width: '100%', resize: 'vertical', boxSizing: 'border-box', marginBottom: '10px' }} />
                    <button onClick={submitReview} disabled={rvBusy}
                      style={{ width: '100%', padding: '12px', background: rvBusy ? '#6b7280' : OG, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: rvBusy ? 'not-allowed' : 'pointer', fontFamily: "'Tajawal', sans-serif" }}>
                      {rvBusy ? (ar ? 'جاري الإرسال...' : 'Sending...') : (ar ? 'إرسال الرأي' : 'Submit review')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* اطلب الباقة */}
          <button onClick={onOpenWA}
            style={{ width: '100%', padding: '15px', background: 'linear-gradient(135deg,#25D366,#128C7E)', color: 'white', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 6px 20px rgba(37,211,102,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <MessageCircle size={19} /> {ar ? 'اطلب الباقة دلوقتي' : 'Order this package now'}
          </button>
        </div>
      </div>
    </Shell>
  );
}

/* ════════════════════════════════════════════════════════════
   الصفحة الرئيسية للأجنسي (تبويبات: الباقات / أعمالنا)
   ════════════════════════════════════════════════════════════ */
export default function Agency({ services = [], serviceReviews = [], agencyWorks = [], lang, loading, t, onSetPage, onOpenWA, isRTL, onSubmitReview }) {
  const ar = lang === 'ar';
  const [tab, setTab] = useState('packages');
  const [selected, setSelected] = useState(null);
  const [workVideo, setWorkVideo] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds]   = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  const toggleCompare = (id) => {
    setCompareIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 4) return prev;
      return [...prev, id];
    });
  };

  const sortedServices = useMemo(
    () => [...services].sort((a, b) => (a.order_num || 0) - (b.order_num || 0)),
    [services]
  );
  const activeWorks = useMemo(
    () => agencyWorks.filter(w => w.is_active !== false).sort((a, b) => (a.order_num || 0) - (b.order_num || 0)),
    [agencyWorks]
  );

  if (showCompare) {
    const cmpServices = compareIds.map(id => services.find(s => s.id === id)).filter(Boolean);
    return <CompareTable services={cmpServices} lang={lang} isRTL={isRTL} t={t}
      onBack={() => setShowCompare(false)} onOpenWA={onOpenWA}
      onOpenPackage={(id) => { setShowCompare(false); setSelected(id); }} />;
  }

  if (selected) {
    const svc = services.find(s => s.id === selected);
    if (svc) {
      return <PackageDetail service={svc} reviews={serviceReviews} lang={lang} isRTL={isRTL} t={t}
        onBack={() => setSelected(null)} onOpenWA={onOpenWA} onSubmitReview={onSubmitReview} />;
    }
  }

  const TABS = [
    { id: 'packages', label: `📦 ${ar ? 'الباقات' : 'Packages'}`, icon: Briefcase },
    { id: 'works',    label: `🎬 ${ar ? 'أعمالنا' : 'Our Work'}`,  icon: Film },
  ];

  return (
    <Shell>
      {/* العنوان */}
      <div style={{ textAlign: 'center', color: 'white', marginBottom: '24px', animation: 'fadeInDown .6s ease both' }}>
        <h2 style={{ fontSize: '40px', fontWeight: 'bold', textShadow: '2px 2px 12px rgba(0,0,0,0.25)' }}>💼 {t.agency}</h2>
        <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: '15px', marginTop: '6px' }}>
          {tab === 'packages'
            ? `${services.length} ${ar ? 'باقة احترافية' : 'professional packages'}`
            : `${activeWorks.length} ${ar ? 'عمل سابق' : 'completed projects'}`}
        </p>
      </div>

      {/* التبويبات */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '28px', flexWrap: 'wrap' }}>
        {TABS.map(tb => {
          const on = tab === tb.id;
          return (
            <button key={tb.id} onClick={() => setTab(tb.id)}
              style={{ padding: '10px 24px', borderRadius: '24px', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold', fontFamily: "'Tajawal', sans-serif", transition: 'all .25s',
                background: on ? 'white' : 'rgba(255,255,255,0.16)', color: on ? OR : 'white', backdropFilter: 'blur(6px)',
                boxShadow: on ? '0 6px 18px rgba(0,0,0,0.2)' : 'none' }}>
              {tb.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', margin: '0 auto', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : tab === 'packages' ? (
        sortedServices.length === 0 ? (
          <EmptyState icon="📦" text={ar ? 'لا توجد باقات بعد' : 'No packages yet'} />
        ) : (
          <>
          {/* شريط أدوات المقارنة */}
          {sortedServices.length >= 2 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
              {!compareMode ? (
                <button onClick={() => { setCompareMode(true); setCompareIds([]); }}
                  style={{ padding: '11px 24px', background: 'rgba(255,255,255,0.95)', color: OR, border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', boxShadow: '0 4px 14px rgba(0,0,0,0.18)' }}>
                  ⚖️ {ar ? 'قارن بين الباقات' : 'Compare packages'}
                </button>
              ) : (
                <>
                  <div style={{ background: 'rgba(255,255,255,0.95)', color: '#374151', padding: '10px 18px', borderRadius: '12px', fontSize: '13px', fontWeight: 'bold', boxShadow: '0 4px 14px rgba(0,0,0,0.18)' }}>
                    {ar ? `اختر الباقات للمقارنة (${compareIds.length}/4)` : `Select packages (${compareIds.length}/4)`}
                  </div>
                  <button onClick={() => setShowCompare(true)} disabled={compareIds.length < 2}
                    style={{ padding: '11px 24px', background: compareIds.length < 2 ? 'rgba(255,255,255,0.35)' : 'linear-gradient(135deg,#10b981,#059669)', color: compareIds.length < 2 ? 'rgba(255,255,255,0.7)' : 'white', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 'bold', cursor: compareIds.length < 2 ? 'not-allowed' : 'pointer', boxShadow: compareIds.length < 2 ? 'none' : '0 4px 14px rgba(16,185,129,0.4)' }}>
                    {ar ? 'اعرض المقارنة ←' : 'Show comparison ←'}
                  </button>
                  <button onClick={() => { setCompareMode(false); setCompareIds([]); }}
                    style={{ padding: '11px 18px', background: 'rgba(255,255,255,0.14)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '12px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                    {ar ? 'إلغاء' : 'Cancel'}
                  </button>
                </>
              )}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(270px,1fr))', gap: '22px' }}>
            {sortedServices.map((s, index) => {
              const name = ar ? s.name_ar : (s.name_en || s.name_ar);
              const features = Array.isArray(s.features) ? s.features : [];
              const duration = ar ? s.duration_ar : (s.duration_en || s.duration_ar);
              const inCompare = compareIds.includes(s.id);
              return (
                <div key={s.id} style={{ animation: 'cardRise .55s cubic-bezier(.22,1,.36,1) both', animationDelay: `${(index % 9) * 0.07}s` }}>
                  <div onClick={() => compareMode ? toggleCompare(s.id) : setSelected(s.id)}
                    className="gw-card"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '20px', overflow: 'hidden', boxShadow: inCompare ? '0 0 0 2px #10b981, 0 10px 32px rgba(16,185,129,0.3)' : '0 10px 30px rgba(0,0,0,0.3)', cursor: 'pointer', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                    {compareMode && (
                      <div style={{ position: 'absolute', top: '12px', [isRTL ? 'left' : 'right']: '12px', zIndex: 4, width: '30px', height: '30px', borderRadius: '50%', background: inCompare ? '#10b981' : 'rgba(255,255,255,0.9)', border: inCompare ? 'none' : '2px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
                        {inCompare && <Check size={17} color="white" strokeWidth={3} />}
                      </div>
                    )}
                    {s.is_featured && (
                      <div style={{ position: 'absolute', top: '12px', [isRTL ? 'right' : 'left']: '12px', zIndex: 2, background: '#fbbf24', color: '#78350f', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                        ⭐ {ar ? 'الأكثر طلبًا' : 'Popular'}
                      </div>
                    )}
                    <div style={{ background: G2, padding: '28px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: '-20px', [isRTL ? 'left' : 'right']: '-20px', fontSize: '80px', opacity: 0.14 }}>{s.icon || '💼'}</div>
                      <div style={{ fontSize: '52px', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' }}>{s.icon || '💼'}</div>
                      <h3 style={{ color: 'white', fontSize: '17px', fontWeight: 'bold', marginTop: '8px', textShadow: '0 1px 4px rgba(0,0,0,0.25)' }}>{name}</h3>
                    </div>
                    <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                      {features.length > 0 && (
                        <div style={{ marginBottom: '14px', display: 'grid', gap: '6px' }}>
                          {features.slice(0, 3).map((f, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', color: 'rgba(255,255,255,0.75)' }}>
                              <Check size={14} color="#34d399" strokeWidth={3} style={{ flexShrink: 0 }} />
                              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{typeof f === 'string' ? f : (f.text || '')}</span>
                            </div>
                          ))}
                          {features.length > 3 && (
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', paddingRight: '21px' }}>+{features.length - 3} {ar ? 'مميزات أخرى' : 'more'}</div>
                          )}
                        </div>
                      )}
                      <div style={{ marginTop: 'auto' }}>
                        {duration && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '10px' }}>
                            <Clock size={13} /> {duration}
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>{t.from}</span>
                          <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff' }}>${s.price}</span>
                        </div>
                        <button style={{ width: '100%', padding: '11px', background: compareMode ? (inCompare ? 'linear-gradient(135deg,#10b981,#059669)' : 'rgba(255,255,255,0.1)') : G2, color: compareMode && !inCompare ? 'rgba(255,255,255,0.7)' : 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', boxShadow: compareMode ? 'none' : '0 4px 12px rgba(118,75,162,0.4)' }}>
                          {compareMode ? (inCompare ? (ar ? '✓ مختارة' : '✓ Selected') : (ar ? 'اختر للمقارنة' : 'Select')) : (ar ? 'عرض التفاصيل' : 'View details')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )
      ) : (
        /* ── أعمالنا ── */
        activeWorks.length === 0 ? (
          <EmptyState icon="🎬" text={ar ? 'لا توجد أعمال بعد — قريبًا!' : 'No work yet — coming soon!'} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '22px' }}>
            {activeWorks.map((w, index) => (
              <div key={w.id} style={{ animation: 'cardRise .55s cubic-bezier(.22,1,.36,1) both', animationDelay: `${(index % 9) * 0.07}s` }}>
                <div className="gw-card" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
                  <div style={{ height: '180px', background: '#000', position: 'relative' }}>
                    {workVideo === w.id
                      ? <SafeVideo url={w.video_url} />
                      : (
                        <div onClick={() => setWorkVideo(w.id)} style={{ width: '100%', height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                          {w.thumbnail_url
                            ? <img src={w.thumbnail_url} alt={w.title_ar} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <div style={{ width: '100%', height: '100%', background: G2 }} />}
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.32)' }}>
                            <div style={{ width: '58px', height: '58px', borderRadius: '50%', background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.35)' }}>
                              <Play size={24} color={OR} fill={OR} style={{ marginRight: isRTL ? 0 : '-3px', marginLeft: isRTL ? '-3px' : 0 }} />
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                  <div style={{ padding: '16px' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#fff', margin: '0 0 6px' }}>{w.title_ar}</h3>
                    {w.description_ar && <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: '1.7', margin: '0 0 10px' }}>{w.description_ar}</p>}
                    {(() => {
                      const linkedSvc = w.service_id ? services.find(s => s.id === w.service_id) : null;
                      if (!linkedSvc) return null;
                      return (
                        <button onClick={() => setSelected(linkedSvc.id)}
                          style={{ width: '100%', padding: '10px', background: OG, color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '4px', boxShadow: '0 4px 12px rgba(249,115,22,0.35)' }}>
                          {linkedSvc.icon || '📦'} {ar ? `شوف باقة "${linkedSvc.name_ar}"` : `View "${linkedSvc.name_en || linkedSvc.name_ar}"`}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <div style={{ textAlign: 'center', marginTop: '32px' }}>
        <button onClick={() => onSetPage('home')} style={{ padding: '11px 28px', background: 'rgba(255,255,255,0.95)', color: OR, border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}>{t.backHome}</button>
      </div>
    </Shell>
  );
}

/* ════════════════════════════════════════════════════════════
   جدول المقارنة بين الباقات
   ════════════════════════════════════════════════════════════ */
function CompareTable({ services, lang, isRTL, t, onBack, onOpenWA, onOpenPackage }) {
  const ar = lang === 'ar';

  const allFeatures = useMemo(() => {
    const set = [];
    services.forEach(s => {
      const feats = Array.isArray(s.features) ? s.features : [];
      feats.forEach(f => {
        const txt = (typeof f === 'string' ? f : (f.text || '')).trim();
        if (txt && !set.includes(txt)) set.push(txt);
      });
    });
    return set;
  }, [services]);

  const hasFeature = (s, feat) => {
    const feats = Array.isArray(s.features) ? s.features : [];
    return feats.some(f => (typeof f === 'string' ? f : (f.text || '')).trim() === feat);
  };

  const cheapestId = useMemo(() => {
    let id = null, min = Infinity;
    services.forEach(s => { const p = Number(s.price) || 0; if (p < min) { min = p; id = s.id; } });
    return id;
  }, [services]);

  const cellBase = { padding: '14px 12px', textAlign: 'center', borderBottom: '1px solid #f1f5f9', fontSize: '13.5px' };
  const labelCell = { padding: '14px 16px', textAlign: isRTL ? 'right' : 'left', borderBottom: '1px solid #f1f5f9', fontSize: '13.5px', fontWeight: 'bold', color: '#374151', background: '#f9fafb', position: 'sticky', [isRTL ? 'right' : 'left']: 0, zIndex: 1, minWidth: '130px' };

  return (
    <Shell>
      <button onClick={onBack}
        style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: '10px', padding: '8px 16px', color: 'white', cursor: 'pointer', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '18px', fontWeight: 'bold' }}>
        <ArrowRight size={15} style={{ transform: isRTL ? 'none' : 'rotate(180deg)' }} /> {ar ? 'رجوع للباقات' : 'Back to packages'}
      </button>

      <div style={{ textAlign: 'center', color: 'white', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '30px', fontWeight: 'bold', textShadow: '2px 2px 12px rgba(0,0,0,0.25)', margin: 0 }}>⚖️ {ar ? 'مقارنة الباقات' : 'Package Comparison'}</h2>
        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginTop: '6px' }}>{ar ? `مقارنة بين ${services.length} باقات` : `Comparing ${services.length} packages`}</p>
      </div>

      <div style={{ background: 'white', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 18px 50px rgba(0,0,0,0.35)', animation: 'cardRise .5s ease both' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: `${130 + services.length * 150}px` }}>
            <thead>
              <tr>
                <th style={{ ...labelCell, background: '#0f1640', color: 'white', borderBottom: 'none' }}></th>
                {services.map(s => (
                  <th key={s.id} style={{ padding: '18px 12px', textAlign: 'center', background: s.id === cheapestId ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#f97316,#fb923c)', color: 'white', position: 'relative', minWidth: '150px' }}>
                    {s.is_featured && <div style={{ fontSize: '10px', background: '#fbbf24', color: '#78350f', display: 'inline-block', padding: '2px 8px', borderRadius: '10px', marginBottom: '6px', fontWeight: 'bold' }}>⭐ {ar ? 'الأكثر طلبًا' : 'Popular'}</div>}
                    <div style={{ fontSize: '30px' }}>{s.icon || '💼'}</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>{ar ? s.name_ar : (s.name_en || s.name_ar)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={labelCell}>💰 {ar ? 'السعر' : 'Price'}</td>
                {services.map(s => (
                  <td key={s.id} style={{ ...cellBase, fontWeight: 'bold' }}>
                    <span style={{ fontSize: '22px', color: '#f97316' }}>${s.price}</span>
                    {s.id === cheapestId && services.length > 1 && <div style={{ fontSize: '10px', color: '#059669', fontWeight: 'bold', marginTop: '2px' }}>{ar ? '✓ الأوفر' : '✓ Best value'}</div>}
                  </td>
                ))}
              </tr>
              <tr>
                <td style={labelCell}>⏱️ {ar ? 'مدة التنفيذ' : 'Duration'}</td>
                {services.map(s => {
                  const dur = ar ? s.duration_ar : (s.duration_en || s.duration_ar);
                  return <td key={s.id} style={cellBase}>{dur || <span style={{ color: '#cbd5e1' }}>—</span>}</td>;
                })}
              </tr>
              <tr>
                <td style={labelCell}>🎬 {ar ? 'فيديو توضيحي' : 'Demo video'}</td>
                {services.map(s => (
                  <td key={s.id} style={cellBase}>
                    {s.video_url
                      ? <Check size={18} color="#10b981" strokeWidth={3} style={{ display: 'inline' }} />
                      : <span style={{ color: '#e2e8f0', fontSize: '18px', fontWeight: 'bold' }}>✕</span>}
                  </td>
                ))}
              </tr>
              {allFeatures.length > 0 && (
                <tr>
                  <td colSpan={services.length + 1} style={{ padding: '10px 16px', background: '#fff7ed', fontSize: '13px', fontWeight: 'bold', color: '#c2410c', textAlign: isRTL ? 'right' : 'left' }}>
                    ✨ {ar ? 'المميزات' : 'Features'}
                  </td>
                </tr>
              )}
              {allFeatures.map((feat, i) => (
                <tr key={i}>
                  <td style={{ ...labelCell, fontWeight: '500', fontSize: '13px' }}>{feat}</td>
                  {services.map(s => (
                    <td key={s.id} style={cellBase}>
                      {hasFeature(s, feat)
                        ? <Check size={18} color="#10b981" strokeWidth={3} style={{ display: 'inline' }} />
                        : <span style={{ color: '#e2e8f0', fontSize: '18px', fontWeight: 'bold' }}>✕</span>}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td style={{ ...labelCell, background: 'white' }}></td>
                {services.map(s => (
                  <td key={s.id} style={{ ...cellBase, padding: '16px 12px' }}>
                    <button onClick={() => onOpenPackage(s.id)}
                      style={{ width: '100%', padding: '10px', background: 'linear-gradient(135deg,#f97316,#fb923c)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '12.5px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '6px' }}>
                      {ar ? 'التفاصيل' : 'Details'}
                    </button>
                    <button onClick={onOpenWA}
                      style={{ width: '100%', padding: '10px', background: 'linear-gradient(135deg,#25D366,#128C7E)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '12.5px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                      <MessageCircle size={13} /> {ar ? 'اطلب' : 'Order'}
                    </button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {allFeatures.length === 0 && (
        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginTop: '16px' }}>
          {ar ? '💡 لو ضفت مميزات للباقات من لوحة التحكم، هتظهر هنا للمقارنة التفصيلية' : '💡 Add features to packages to see detailed comparison'}
        </p>
      )}
    </Shell>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '18px', padding: '50px 24px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)', maxWidth: '460px', margin: '0 auto' }}>
      <div style={{ fontSize: '56px', marginBottom: '14px' }}>{icon}</div>
      <p style={{ color: 'white', fontSize: '16px', margin: 0 }}>{text}</p>
    </div>
  );
}
